import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Soup, Beef, Salad, CakeSlice, Utensils, Fish, Carrot, Leaf, IceCream, Cake, Cookie, Cherry, Heart, Sparkles, Timer, Star } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useLanguage } from '../context/LanguageContext';
import { COLORS, IMAGES, fadeInUp, staggerContainer, fadeIn } from '../config';

const iconMap = {
  soup: (k) => <Soup key={k} size={18} />,
  beef: (k) => <Beef key={k} size={18} />,
  salad: (k) => <Salad key={k} size={18} />,
  cakeSlice: (k) => <CakeSlice key={k} size={18} />,
  utensils: (k) => <Utensils key={k} size={18} />,
  fish: (k) => <Fish key={k} size={18} />,
  carrot: (k) => <Carrot key={k} size={18} />,
  leaf: (k) => <Leaf key={k} size={18} />,
  iceCream: (k) => <IceCream key={k} size={18} />,
  cake: (k) => <Cake key={k} size={18} />,
  cookie: (k) => <Cookie key={k} size={18} />,
  cherry: (k) => <Cherry key={k} size={18} />,
};

const tabIconMap = {
  primi: <Soup size={18} />,
  secondi: <Beef size={18} />,
  contorni: <Salad size={18} />,
  dolci: <CakeSlice size={18} />,
};

const Menu = () => {
  const { language, t } = useLanguage();
  const [activeTab, setActiveTab] = useState('primi');
  const [hoveredItem, setHoveredItem] = useState(null);
  const [menuData, setMenuData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/menu.json')
      .then((res) => res.json())
      .then((data) => {
        setMenuData(data);
        if (data.categorie && data.categorie.length > 0) {
          setActiveTab(data.categorie[0].id);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const getItemIcon = (iconName, key) => {
    const factory = iconMap[iconName];
    return factory ? factory(key) : <Utensils key={key} size={18} />;
  };

  const categories = menuData?.categorie || [];

  return (
    <>
      {/* Hero Banner */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${IMAGES.pasta})` }}
        />
        <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, rgba(114, 47, 55, 0.9) 0%, rgba(30, 58, 95, 0.85) 100%)` }} />
        <div className="relative z-10 text-center px-4">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-['Cormorant_Garamond'] text-5xl md:text-6xl text-white mb-4"
          >
            {t('menuPage.hero.title')}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl italic"
            style={{ color: COLORS.gold }}
          >
            {t('menuPage.hero.subtitle')}
          </motion.p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16" style={{ backgroundColor: COLORS.vaniglia }}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-lg leading-relaxed"
            style={{ color: COLORS.nero }}
            dangerouslySetInnerHTML={{ __html: t('menuPage.intro') }}
          />
        </div>
      </section>

      {/* Menu Section */}
      <section className="py-24" style={{ backgroundColor: COLORS.navy }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <motion.p
              variants={fadeInUp}
              className="text-xs tracking-[0.2em] font-semibold uppercase mb-4"
              style={{ color: COLORS.gold }}
            >
              <Sparkles className="inline mr-2" size={14} />
              {t('menu.overline')}
              <Sparkles className="inline ml-2" size={14} />
            </motion.p>
            <motion.h2
              variants={fadeInUp}
              className="font-['Cormorant_Garamond'] text-4xl md:text-5xl mb-4"
              style={{ color: COLORS.vaniglia }}
            >
              {t('menu.title')}
            </motion.h2>
            <motion.div variants={fadeInUp} className="w-16 h-1 mx-auto mb-6" style={{ backgroundColor: COLORS.gold }} />
            <motion.p
              variants={fadeInUp}
              className="max-w-xl mx-auto flex items-center justify-center gap-2"
              style={{ color: `${COLORS.vaniglia}b3` }}
            >
              <Timer size={16} style={{ color: COLORS.gold }} />
              {t('menu.subtitle')}
            </motion.p>

            {menuData?.aggiornato_il && (
              <motion.p
                variants={fadeInUp}
                className="mt-3 text-xs tracking-wider"
                style={{ color: `${COLORS.vaniglia}60` }}
                data-testid="menu-last-updated"
              >
                {language === 'it' ? 'Aggiornato il' : 'Last updated'}: {menuData.aggiornato_il}
              </motion.p>
            )}
          </motion.div>

          {/* Tabs - Dynamic from JSON */}
          {loading ? (
            <div className="text-center py-16">
              <p style={{ color: COLORS.vaniglia }}>Loading...</p>
            </div>
          ) : (
            <motion.div
              variants={fadeIn}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="flex flex-wrap justify-center gap-3 bg-transparent mb-12">
                  {categories.map((cat) => (
                    <TabsTrigger
                      key={cat.id}
                      value={cat.id}
                      className="flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-all transform hover:scale-105"
                      style={{
                        backgroundColor: activeTab === cat.id ? COLORS.gold : 'transparent',
                        color: activeTab === cat.id ? COLORS.navy : COLORS.vaniglia,
                        border: activeTab === cat.id ? 'none' : `2px solid ${COLORS.gold}40`,
                        boxShadow: activeTab === cat.id ? '0 4px 15px rgba(0,0,0,0.2)' : 'none'
                      }}
                      data-testid={`menu-tab-${cat.id}`}
                    >
                      <span className={activeTab === cat.id ? 'animate-pulse' : ''}>
                        {tabIconMap[cat.id] || <Utensils size={18} />}
                      </span>
                      {language === 'it' ? cat.nome_it : cat.nome_en}
                    </TabsTrigger>
                  ))}
                </TabsList>

                {categories.map((cat) => (
                  <TabsContent key={cat.id} value={cat.id}>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                      {/* Category Image */}
                      <div className="lg:col-span-1">
                        <motion.img
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          src={cat.immagine}
                          alt={language === 'it' ? cat.nome_it : cat.nome_en}
                          className="rounded-xl w-full h-full min-h-[300px] object-contain"
                          style={{ backgroundColor: COLORS.vaniglia }}
                        />
                      </div>

                      {/* Menu Items */}
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                        className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4"
                      >
                        {cat.piatti.map((piatto, index) => {
                          const itemKey = `${cat.id}-${index}`;
                          const dishName = language === 'it' ? piatto.nome_it : piatto.nome_en;
                          return (
                            <motion.div
                              key={itemKey}
                              whileHover={{ scale: 1.02, y: -4 }}
                              onHoverStart={() => setHoveredItem(itemKey)}
                              onHoverEnd={() => setHoveredItem(null)}
                              className="group backdrop-blur-sm rounded-xl px-6 py-5 cursor-pointer transition-all"
                              style={{
                                backgroundColor: `${COLORS.vaniglia}15`,
                                border: `1px solid ${COLORS.vaniglia}15`,
                                color: COLORS.vaniglia
                              }}
                              data-testid={`menu-item-${cat.id}-${index}`}
                            >
                              <div className="flex items-center gap-3">
                                <span
                                  className={`transition-transform ${hoveredItem === itemKey ? 'scale-125 rotate-12' : ''}`}
                                  style={{ color: COLORS.sage }}
                                >
                                  {getItemIcon(piatto.icona, index)}
                                </span>
                                <span className="font-medium flex-1">{dishName}</span>
                                <Heart
                                  size={16}
                                  className={`transition-all ${hoveredItem === itemKey ? 'opacity-100' : 'opacity-0'}`}
                                  style={{ color: COLORS.sage }}
                                />
                              </div>
                            </motion.div>
                          );
                        })}
                      </motion.div>
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            </motion.div>
          )}

          {/* Decorative element removed */}
        </div>
      </section>

      {/* Food Gallery */}
      <section className="py-24" style={{ backgroundColor: COLORS.vaniglia }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <motion.h2
              variants={fadeInUp}
              className="font-['Cormorant_Garamond'] text-4xl md:text-5xl mb-4"
              style={{ color: COLORS.redwine }}
            >
              {t('menuPage.gallery.title')}
            </motion.h2>
            <motion.div variants={fadeInUp} className="w-16 h-1 mx-auto" style={{ backgroundColor: COLORS.gold }} />
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {categories.slice(0, 4).map((cat, index) => (
              <motion.div
                key={cat.id}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="overflow-hidden rounded-xl"
              >
                <img
                  src={cat.immagine}
                  alt={language === 'it' ? cat.nome_it : cat.nome_en}
                  className="w-full h-48 md:h-64 object-contain hover:scale-110 transition-transform duration-500"
                  style={{ backgroundColor: COLORS.vaniglia }}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Menu;
